import useAccountEnhanced from "src/hooks/use-account-enhanced";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useCreateOrderDocument } from "src/api/mvte-query-options";

type FileUploadInputs = {
    documentType: string;
    dispatchNumber: string;
    files: FileList;
};

export function OrderUploadDocumentForm({
    orderNumber,
    onSuccess,
}: {
    orderNumber: string;
    onSuccess: () => void;
}) {
    const createOrderDocumentMutation = useCreateOrderDocument({ orderNumber });
    const form = useForm<FileUploadInputs>();
    const accountEnhanced = useAccountEnhanced();

    async function onSubmit({
        documentType,
        dispatchNumber,
        files,
    }: FileUploadInputs) {
        try {
            const file = files.item(0) as File;
            const fileSizeInMB = file.size / (1024 ** 2);
            const MAX_FILE_SIZE_MB = 5;

            if (fileSizeInMB > MAX_FILE_SIZE_MB) {
                form.setError("root", {
                    message: "File size exceeds 5MB limit",
                });
                return;
            }

            const fileBytes = await new Promise<number[]>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const arrayBuffer = reader.result;
                    const bytes = new Uint8Array(arrayBuffer as ArrayBuffer);
                    resolve(Array.from(bytes));
                };
                reader.onerror = () => reject(reader.error);
                reader.readAsArrayBuffer(files.item(0) as File);
            });

            await createOrderDocumentMutation.mutateAsync({
                AppSource: "BillingFileUpload",
                AppVersion: "0.0.10",
                CreatedBy: accountEnhanced.me.employeeCode,
                DispatchNumber: dispatchNumber,
                DocumentType: documentType,
                DriverCode: "NONE", // TODO: Drive code is not present in any of the db records
                FileData: Array.from(fileBytes),
                FileName: file.name,
                OrderNumber: orderNumber,
            });

            form.reset();
            toast.success("File uploaded successfully");

            onSuccess();
        } catch (error) {
            toast.error("Failed to upload file");
        }
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            {form.formState.errors.root && (
                <div className="alert alert-error alert-sm ">
                    {form.formState.errors.root.message}
                </div>
            )}
            {/* Order Number */}
            <fieldset className="fieldset w-full">
                <legend className="fieldset-legend">Order Number</legend>
                <input
                    type="text"
                    defaultValue={orderNumber}
                    disabled
                    className="input input-bordered input-xs w-full"
                />
            </fieldset>
            {/* Document Type */}
            <fieldset className="fieldset w-full">
                <legend className="fieldset-legend">Document Type</legend>
                <select
                    className="select select-bordered select-xs w-full"
                    {...form.register("documentType", { required: true })}
                >
                    <option value="">Select Document Type</option>
                    <option value="RCS">RCS</option>
                    <option value="SBL">SBL</option>
                    <option value="CBL">CBL</option>
                    <option value="MSC">MSC</option>
                    <option value="TRIP">TRIP</option>
                    <option value="WGT">WGT</option>
                    <option value="LMP">LMP</option>
                    <option value="FTS">FTS</option>
                </select>
            </fieldset>
            {/* Dispatch Type */}
            <fieldset className="fieldset w-full">
                <legend className="fieldset-legend">Dispatch Number</legend>
                <select
                    className="select select-bordered select-xs w-full"
                    {...form.register("dispatchNumber", { required: true })}
                >
                    <option value="">Select Dispatch Number</option>
                    <option value="01">01</option>
                    <option value="02">02</option>
                    <option value="03">03</option>
                    <option value="04">04</option>
                    <option value="05">05</option>
                    <option value="06">06</option>
                </select>
            </fieldset>
            {/* Upload File */}
            <fieldset className="fieldset w-full">
                <legend className="fieldset-legend">File</legend>
                <input
                    type="file"
                    className="file-input file-input-xs file-input-bordered w-full"
                    {...form.register("files", { required: true })}
                />
            </fieldset>
            <button
                className="mt-4 btn btn-neutral btn-sm"
                disabled={
                    form.formState.isSubmitting || orderNumber === undefined
                }
            >
                {form.formState.isSubmitting ? (
                    <>
                        <span className="loading loading-spinner"></span>
                        loading
                    </>
                ) : (
                    "Submit"
                )}
            </button>
        </form>
    );
}
